import { useEffect, useRef, useState } from 'react';
import { Search, Ticket, Footprints } from 'lucide-react';

const steps = [
  {
    n: '01',
    title: 'TROUVE',
    description: "Parcours, dénivelé, saison, niveau : filtre parmi 2 400 courses et trouve celle qui te ressemble.",
    colorVar: '--primary',
    color: '#caff00',
    icon: Search,
  },
  {
    n: '02',
    title: 'RÉSERVE',
    description: "Un seul compte, paiement sécurisé, certificat médical stocké. Ton dossard en 2 min chrono.",
    colorVar: '--accent',
    color: '#00e5ff',
    icon: Ticket,
  },
  {
    n: '03',
    title: 'KOURS',
    description: "Retrouve tes dossards, ta communauté et tes chronos dans l'appli. GPS, coaches, tout est là.",
    colorVar: '--energy',
    color: '#ff2d9e',
    icon: Footprints,
  },
];

const HowItWorksSection = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const el = gridRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      if (!w) return;
      const idx = Math.round(el.scrollLeft / w);
      setActive(Math.min(steps.length - 1, Math.max(0, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isMobile]);

  const scrollTo = (i: number) => {
    const el = gridRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  return (
    <section id="how-it-works" className="relative py-24 md:py-32 border-t border-white/10 overflow-hidden bg-background">
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="text-center mb-16 md:mb-20">
          <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-energy mb-4">
            // CHAPITRE UN · LE MODE D'EMPLOI
          </div>
          <h2 className="m-0 leading-[0.95]">
            <span className="font-display italic uppercase -skew-x-6 inline-block text-[56px] md:text-[80px] lg:text-[104px] text-foreground">
              3 ÉTAPES,{' '}
            </span>
            <span
              className="font-display italic uppercase -skew-x-6 inline-block text-[56px] md:text-[80px] lg:text-[104px] text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #00e5ff 0%, #caff00 100%)' }}
            >
              1 DOSSARD.
            </span>
          </h2>
        </div>

        <div ref={gridRef} className="k-steps-grid relative grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            aria-hidden
            className="k-steps-line hidden md:block absolute top-[50px] left-[16.66%] right-[16.66%] h-px opacity-30"
            style={{ background: 'linear-gradient(90deg, #caff00, #00e5ff, #ff2d9e)' }}
          />
          {steps.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.n}
                className="k-step-card relative overflow-hidden rounded-2xl border border-white/10 bg-card/60 px-7 py-10 md:px-8 md:py-10 transition-colors duration-300 hover:border-white/20"
              >
                <div
                  aria-hidden
                  className="absolute -top-8 right-2 font-display italic uppercase text-[140px] leading-none opacity-[0.12] pointer-events-none"
                  style={{ color: `hsl(var(${s.colorVar}))` }}
                >
                  {s.n}
                </div>
                <div
                  className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-4 border-background font-display italic font-black text-xl"
                  style={{
                    background: `hsl(var(${s.colorVar}))`,
                    color: '#0a0a0c',
                    boxShadow: `0 0 0 1px hsl(var(${s.colorVar})), 0 8px 30px hsl(var(${s.colorVar}) / 0.25)`,
                  }}
                >
                  {s.n}
                </div>
                <div className="relative z-10 mt-7 flex items-center gap-3">
                  <Icon className="w-6 h-6 text-foreground/70" />
                  <div className="font-display italic uppercase text-3xl text-foreground">{s.title}.</div>
                </div>
                <p className="relative z-10 mt-4 text-[14px] leading-relaxed text-muted-foreground">
                  {s.description}
                </p>
              </div>
            );
          })}
        </div>

        <div className="k-steps-swipe">
          <div className="k-steps-dots" role="tablist" aria-label="Étapes">
            {steps.map((s, i) => (
              <button
                key={s.n}
                role="tab"
                aria-selected={active === i}
                aria-label={`Étape ${i + 1}`}
                onClick={() => scrollTo(i)}
                className={`k-steps-dot ${active === i ? 'is-active' : ''}`}
                style={{ ['--dot-color' as any]: s.color }}
              />
            ))}
          </div>
          <div className="k-steps-hint">
            <span className="k-steps-hint__arrow">‹</span>
            <span>Swipe pour découvrir les 3 étapes</span>
            <span className="k-steps-hint__arrow">›</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
